import {
  AirAirlineDTO,
  AirEventDTO,
  AirLocationDTO,
  AirResponseDTO,
  AirRouteSegmentDTO,
  AirShipmentDTO,
} from "./response.types";
import { SeaRouteDetailsDTO } from "entities/SeaSearchCard/api/response.types";
import { AirEventCodesDTO, AirMessagesDTO, AirMessagesModel, AirStatusesModel } from "entities/shared/airCard.types";
import { SeaTransportTypesDTO } from "entities/shared/seaCard.types";
import {
  CoordinateModelDTO,
  DateTimeDTO,
  SearchCoordinateDTO,
  SearchReferenceDTO,
} from "entities/shared/shipmentCard.types";
import { isNumber, Nullable, ValueOf } from "shared/lib/typeCheck.lib";

export type AirTransformedEventDTO = { location: string | null; events: AirEventDTO[] };

const API_KEY_LIMIT_CODES = [
  AirMessagesModel.API_KEY_LIMIT_REACHED,
  AirMessagesModel.API_KEY_QUOTA_EXCEEDED,
  AirMessagesModel.API_KEY_RATE_LIMIT_EXCEEDED,
  AirMessagesModel.INVALID_API_KEY,
];

export class AirSearchApiParser {
  private model: AirResponseDTO;

  private _etd: string | null = null;
  private _atd: string | null = null;
  private _eta: string | null = null;
  private _ata: string | null = null;
  private _fromDirection: string | null = null;
  private _toDirection: string | null = null;

  public readonly isSuccess: boolean = false;
  public readonly hasData: boolean = false;
  public readonly requestLimitReached: boolean = false;

  constructor(data: AirResponseDTO) {
    this.model = data;

    this.setDates();
    this.setDirections();

    this.isSuccess =
      this.model.success &&
      (this.message === AirMessagesModel.OK || this.message === "OK") &&
      !!this.model.data?.shipment;

    this.hasData = this.isSuccess && !!this.model.data?.events?.length;

    this.requestLimitReached =
      this.model.requestLimitReachedWebApp === true || API_KEY_LIMIT_CODES.includes(this.message);
  }

  get message(): ValueOf<typeof AirMessagesDTO> | string {
    return this.model.status_code;
  }

  get reference(): SearchReferenceDTO | null {
    return this.model.data?.shipment?.awb_number ?? this.model.meta?.request?.params?.awb_number ?? null;
  }

  get airline(): AirAirlineDTO | null {
    return this.model.meta?.airline ?? null;
  }

  get airlineName(): string {
    return this.airline?.name ?? "";
  }

  get shipment(): AirShipmentDTO | null {
    return this.model.data?.shipment ?? null;
  }

  get status(): ValueOf<typeof AirStatusesModel> {
    const events = this.events;
    const finalLocation = this.shipment?.arrival?.location;
    const hasArrivedAtDestination = events.some(
      (event) => event.code === AirEventCodesDTO.ARR && event.location === finalLocation && !!event.date_time.actual
    );

    if (hasArrivedAtDestination || this._ata) {
      return AirStatusesModel.DELIVERED;
    }

    const hasDeparted = events.some(
      (event) =>
        (event.code === AirEventCodesDTO.DEP || event.code === AirEventCodesDTO.ARR) && !!event.date_time.actual
    );

    if (hasDeparted || this._atd) {
      return AirStatusesModel.IN_TRANSIT;
    }

    return AirStatusesModel.PLANNED;
  }

  get fromDirection(): string | null {
    return this._fromDirection;
  }

  get toDirection(): string | null {
    return this._toDirection;
  }

  get locations(): AirLocationDTO[] {
    return this.model.data?.locations ?? [];
  }

  get events(): AirEventDTO[] {
    return [...(this.model.data?.events ?? [])].sort((a, b) => a.sequence - b.sequence);
  }

  get routeSegments(): AirRouteSegmentDTO[] {
    return [...(this.model.data?.route_segments ?? [])].sort((a, b) => a.sequence - b.sequence);
  }

  get pieces(): string | null {
    const quantity = this.shipment?.cargo_details?.quantity;
    if (!isNumber(quantity)) return null;
    return String(quantity);
  }

  /** Numeric piece count for follow / update payloads. */
  get piece(): number {
    const quantity = this.shipment?.cargo_details?.quantity;
    return isNumber(quantity) ? quantity : 0;
  }

  get weight(): number | null {
    const weight = this.shipment?.cargo_details?.weight;
    return isNumber(weight) ? weight : null;
  }

  get iataCode(): string {
    return this.airline?.iata_code ?? "";
  }

  get fromCode(): string {
    return this.getLocationByCode(this.shipment?.departure?.location ?? null)?.iata_code ?? "";
  }

  get toCode(): string {
    return this.getLocationByCode(this.shipment?.arrival?.location ?? null)?.iata_code ?? "";
  }

  get hasPin(): boolean {
    return !!(this.polCoordinate || this.podCoordinate || this.currentPin);
  }

  getLocationByCode(code: Nullable<string>): AirLocationDTO | null {
    if (!code) return null;
    return this.locations.find((location) => location.id === code || location.iata_code === code) ?? null;
  }

  public getLocationName(location: Nullable<AirLocationDTO>): string | null {
    if (!location) return null;
    const city = location.nearest_city || location.name;
    if (city && location.country?.code) {
      return `${city}, ${location.country.code}`;
    }
    return city ?? location.iata_code ?? null;
  }

  get polCoordinate(): CoordinateModelDTO | null {
    return this.getCoordinateByCode(this.shipment?.departure?.location ?? null);
  }

  get podCoordinate(): CoordinateModelDTO | null {
    return this.getCoordinateByCode(this.shipment?.arrival?.location ?? null);
  }

  /** Build MapRoute-compatible route_details (same as main). */
  get routeData(): SeaRouteDetailsDTO | null {
    const segments = this.routeSegments;
    if (!segments.length) return null;

    const route_legs = segments.map((segment) => {
      const path = this.resolveSegmentPath(segment);
      return {
        sequence: segment.sequence,
        route_path: path,
        transport_mode: SeaTransportTypesDTO.AIR,
        from_location_id: undefined,
        to_location_id: undefined,
      };
    });

    if (!route_legs.some((leg) => leg.route_path?.length)) return null;

    return { route_legs };
  }

  /** Locations for MapRoute labels (nested coordinates like main). */
  get mapLocations() {
    return this.locations.map((location, index) => ({
      id: index + 1,
      name: location.nearest_city || location.name,
      countryCode: location.country?.code ?? null,
      coordinates: location.coordinates,
    }));
  }

  get currentPin(): CoordinateModelDTO | null {
    if (this._ata) return this.podCoordinate;
    if (this._atd) {
      const lastActualEvent = [...this.events].reverse().find((event) => !!event.date_time.actual);
      const eventCoordinate = this.getCoordinateByCode(lastActualEvent?.location ?? null);
      if (eventCoordinate) return eventCoordinate;
      return this.polCoordinate;
    }
    return this.polCoordinate;
  }

  get etd(): DateTimeDTO | null {
    return this._etd;
  }

  get atd(): DateTimeDTO | null {
    return this._atd;
  }

  get eta(): DateTimeDTO | null {
    return this._eta;
  }

  get ata(): DateTimeDTO | null {
    return this._ata;
  }

  get searchError(): ValueOf<typeof AirMessagesModel> | string {
    return this.message;
  }

  /** Group events by location for EventList / createAirEvents. */
  public getTransformedEvents(targetEvents?: Nullable<AirEventDTO[]>): AirTransformedEventDTO[] {
    const events = targetEvents ?? this.events;
    if (!events.length) return [];

    const groups: AirTransformedEventDTO[] = [];
    let prevLocation: string | null = null;

    events.forEach((event, index) => {
      const locationName = this.getLocationName(this.getLocationByCode(event.location));

      if (index === 0 || prevLocation !== event.location) {
        groups.push({ location: locationName, events: [event] });
      } else {
        groups[groups.length - 1].events.push(event);
      }

      prevLocation = event.location;
    });

    return groups;
  }

  private getCoordinateByCode(code: Nullable<string>): CoordinateModelDTO | null {
    const location = this.getLocationByCode(code);
    if (!location || !isNumber(location.coordinates?.lat) || !isNumber(location.coordinates?.lng)) {
      return null;
    }
    return { lat: location.coordinates.lat, lng: location.coordinates.lng };
  }

  private resolveSegmentPath(segment: AirRouteSegmentDTO): SearchCoordinateDTO[] {
    if (segment.route_path?.length) {
      return segment.route_path.reduce<SearchCoordinateDTO[]>((acc, point) => {
        if (isNumber(point[0]) && isNumber(point[1])) {
          acc.push([Number(point[0]), Number(point[1])]);
        }
        return acc;
      }, []);
    }

    const from = this.getCoordinateByCode(segment.departure.location);
    const to = this.getCoordinateByCode(segment.arrival.location);
    if (from && to) {
      return [
        [from.lat, from.lng],
        [to.lat, to.lng],
      ];
    }
    return [];
  }

  private setDirections() {
    this._fromDirection = this.getLocationName(this.getLocationByCode(this.shipment?.departure?.location ?? null));
    this._toDirection = this.getLocationName(this.getLocationByCode(this.shipment?.arrival?.location ?? null));
  }

  private setDates() {
    const departure = this.shipment?.departure;
    const arrival = this.shipment?.arrival;

    if (departure?.actual) {
      this._atd = departure.actual;
    } else if (departure?.estimated) {
      this._etd = departure.estimated;
    }

    if (arrival?.actual) {
      this._ata = arrival.actual;
    } else if (arrival?.estimated) {
      this._eta = arrival.estimated;
    }
  }
}
